import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CloseIcon from "@material-ui/icons/Close";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import FiberManualRecordIcon from "@material-ui/icons/FiberManualRecord";
import { Grid, makeStyles, Box, Typography, Dialog } from "@material-ui/core";
import _ from "underscore";

import { api } from "../../services";
import { COLORS } from "../../theme/colors";
import { EXCEL_FILE_TYPE } from "../../constants";
import { header } from "../workspace/settings/sample";
import { NYCMallsPortfolioTable } from "./PortfolioTable";
import SettingHeader from "../../components/SettingHeader";
import { Button, UploadButton } from "../../components/base";
import { MEDIA_SERVICE_FILES_BASE_URL } from "../../services/api";
import { saveNotification } from "../../redux/notification/actions";
import { BouncingDotsLoader } from "../../components/bouncingDotsLoader";
import { GLOBAL_DRAFT_URL } from "../aroundtown/PortfolioRegistrationPage";

export const NYC_MALLS_DRAFT_URL = "/onboarding/nyc-malls/portfolio-registration/draft/";
const NYC_MALLS_TEMPLATE_FILE = "nyc_malls_portfolio_registration_template.xlsx";

export const READY_STATUSES = ["SUCCESS", "FAILURE", "REVOKED"];

export const useStyles = makeStyles((theme) => ({
  popupContent: {
    padding: `${theme.spacing(4)}px ${theme.spacing(1)}px`,
    lineHeight: "2.5rem",
  },
  approveBtn: {
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
    marginLeft: theme.spacing(5),
  },
  buttonGroup: {
    columnGap: 30,
    width: "fit-content",
  },
  calculatedRow: {
    marginBottom: theme.spacing(2),
    columnGap: theme.spacing(2),
    flexWrap: "nowrap",
  },
}));

const POPUP_TYPES = {
  reUploadFile: 1,
  calculate: 2,
};

const ReUploadFilePopupContent = ({ setPopup, loading, onApprove }) => {
  const classes = useStyles();

  return (
    <>
      <Typography variant="h3">Portfolio Registration File Upload</Typography>

      <Typography className={classes.popupContent} align="center" variant="h2">
        The current portfolio data in the platform will be deleted and replaced by the data in the
        file you are uploading. Do you want to proceed?
      </Typography>

      <Grid container justifyContent="flex-end">
        <Button variant="text" text="CANCEL" onClick={() => setPopup()} />
        <Button
          text="CONFIRM"
          loading={loading}
          onClick={() => onApprove()}
          rootStyle={classes.approveBtn}
        />
      </Grid>
    </>
  );
};

const CalculatePopupContent = ({ rowsCount, setPopup }) => {
  const calculatedValues = [
    "Annual photovoltaic power output (kWh/kWp)",
    "Roof - Estimated Installed Capacity (kWp)",
    "Roof - Annual Electricity Production (kWh/Year)",
    "Parking Lots - Estimated Installed Capacity (kWp)",
    "Parking Lots - Annual Electricity Production (kWh/Year)",
  ];
  const classes = useStyles();
  const [calculatingIndex, setCalculatingIndex] = useState(0);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setCalculatingIndex((calculatingIndex) => {
        if (calculatingIndex === calculatedValues.length - 1) {
          clearInterval(intervalId);
        }
        return calculatingIndex + 1;
      });
    }, (rowsCount / calculatedValues.length) * 1000);
  }, []);

  return (
    <>
      <Grid container justifyContent="flex-end">
        <CloseIcon onClick={() => setPopup()} />
      </Grid>

      <Box px={5} mb={5} mt={2}>
        <Typography align="center" variant="h2" style={{ marginBottom: 40 }}>
          Calculating sys. predictions....
        </Typography>
        {calculatedValues.map((t, i) => (
          <Grid className={classes.calculatedRow} key={i} container justifyContent="space-between">
            <Typography
              variant="h4"
              style={{ color: i > calculatingIndex ? COLORS.darks[0][50] : " initial" }}
            >
              {t}
            </Typography>
            {i < calculatingIndex ? (
              <CheckCircleIcon style={{ color: COLORS.green }} />
            ) : i === calculatingIndex ? (
              <BouncingDotsLoader />
            ) : (
              <FiberManualRecordIcon style={{ color: COLORS.darks[0][25] }} />
            )}
          </Grid>
        ))}
      </Box>
    </>
  );
};

export const NYCMallsPortfolioRegistrationPage = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [originalRows, setOriginalRows] = useState([]);
  const [rows, setRows] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadingData, setUploadingData] = useState(false);
  const [draft, setDraft] = useState();
  const [selectedFile, setSelectedFile] = useState();
  const [popup, setPopup] = useState();
  const [isCalculatedDone, setCalculatedDone] = useState(false);
  const { user } = useSelector((state) => state.auth);
  const rowsRef = useRef();
  rowsRef.current = rows;
  const updatedfilesCount = draft?.files?.length;

  useEffect(() => {
    api
      .get(`${NYC_MALLS_DRAFT_URL}task/`)
      .then((r) => r.data)
      .then((data) => {
        if (data.status === READY_STATUSES[0]) {
          setCalculatedDone(true);
        }
      });
  }, []);

  useEffect(() => {
    api
      .get(GLOBAL_DRAFT_URL, { params: { clusters: user.clusters[0].id } })
      .then((resp) => resp.data)
      .then((draft) => {
        setDraft(draft);
        if (draft.buildings) {
          setOriginalRows(draft.buildings);
          setRows(draft.buildings);
        }
      });
  }, []);

  const isRowsDataNotChanged = useMemo(() => {
    return _.isEqual(rows, originalRows);
  }, [rows, originalRows]);

  const checkTask = () => {
    const intervalId = setInterval(() => {
      api
        .get(`${NYC_MALLS_DRAFT_URL}task/`)
        .then((r) => r.data)
        .then((data) => {
          if (READY_STATUSES.includes(data.status)) {
            clearInterval(intervalId);
            setPopup();
          }
          if (data.status === READY_STATUSES[0]) {
            setOriginalRows(data.buildings);
            setRows(data.buildings);
            setCalculatedDone(true);
          }
        });
    }, 5000);
  };

  const onFileChange = (event) => {
    const file = event.target.files[0];
    setSelectedFile(file);
    if (updatedfilesCount) {
      setPopup(POPUP_TYPES.reUploadFile);
    } else {
      uploadFile(file);
    }
  };

  const uploadFile = (file = null) => {
    if (uploading) return;
    const form = new FormData();
    form.append("file", file || selectedFile);
    setUploading(true);
    return api
      .post(`${NYC_MALLS_DRAFT_URL}upload/`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        params: { clusters: user.clusters[0].id },
      })
      .then((res) => res.data)
      .then((data) => {
        setCalculatedDone(false);
        setRows(data.buildings);
        setDraft({
          ...draft,
          files: [
            ...(draft?.files || []),
            {
              id: data.id,
              name: data.name,
              ext: data.ext,
              size: data.size,
              file: data.file,
              created: data.created,
            },
          ],
        });
      })
      .catch((err) => {
        const statusCode = err?.response?.status;
        let message;
        if (statusCode === 400) {
          if ("file" in err.response.data) {
            message = err.response.data["file"][0];
          } else if ("error" in err.response.data) {
            message = err.response.data["error"];
          }
        } else if (statusCode === 500) {
          message =
            "Error when processing the uploaded file. Make sure the uploaded file matched with template";
        } else {
          message = "Something went wrong. Please try again";
        }
        dispatch(saveNotification({ message, error: true }));
      })
      .finally(() => {
        setUploading(false);
        setPopup();
      });
  };

  const saveChanges = () => {
    if (uploadingData) return;
    setPopup(POPUP_TYPES.calculate);
    setCalculatedDone(false);
    setUploadingData(true);
    api
      .post(`${NYC_MALLS_DRAFT_URL}save/`, rowsRef.current, {
        params: { clusters: user.clusters[0].id },
      })
      .then((resp) => resp.data)
      .then((draft) => {
        setOriginalRows(draft.buildings);
        setRows(draft.buildings);
        checkTask();
      })
      .catch((e) => {
        let message = "Oops...Something went wrong.";
        if (e.response && "error" in e.response.data) {
          message = e.response.data.error;
        } else if (e.response.data instanceof Array) {
          message = "Some fields are required";
        }
        dispatch(
          saveNotification({
            message: message,
            error: true,
          })
        );
      })
      .finally(() => {
        setUploadingData(false);
      });
  };

  const deleteBuilding = (id) => {
    api
      .delete(`${GLOBAL_DRAFT_URL}buildings/${id}/`, {
        params: { clusters: user.clusters[0].id },
      })
      .catch(() => {
        dispatch(
          saveNotification({
            message: "Oops...Something went wrong.",
            error: true,
          })
        );
      });
  };

  return (
    <Box mx={3} pt={5}>
      <Grid container justifyContent="space-between" style={{ marginBottom: 30 }}>
        <SettingHeader
          style={{ width: "fit-content" }}
          title={header.portfolioregistration.title}
          subtitle={header.portfolioregistration.subtitle}
          img={header.portfolioregistration.img}
        />

        <Grid
          className={classes.buttonGroup}
          container
          justifyContent="flex-end"
          alignItems="center"
        >
          <UploadButton
            onlyText
            accept={EXCEL_FILE_TYPE}
            loading={uploading}
            onChange={onFileChange}
            text={updatedfilesCount ? "REUPLOAD DATA" : "UPLOAD DATA"}
            disabled={!draft?.is_table_enabled}
          />
          <Button
            variant="text"
            text="DOWNLOAD TEMPLATE"
            href={`${MEDIA_SERVICE_FILES_BASE_URL}/${NYC_MALLS_TEMPLATE_FILE}`}
          />
          <Button
            text="CONFIRM & CALCULATE"
            disabled={
              !draft?.is_table_enabled ||
              !updatedfilesCount ||
              (isRowsDataNotChanged && isCalculatedDone)
            }
            onClick={saveChanges}
            loading={uploadingData}
          />
        </Grid>
      </Grid>

      <NYCMallsPortfolioTable
        rows={rows}
        setRows={setRows}
        disableTable={!draft?.is_table_enabled}
        deleteBuilding={deleteBuilding}
        isCalculatedDone={isCalculatedDone}
      />

      <Dialog open={!!popup} maxWidth="sm">
        <Box mx={5} my={4}>
          {popup === POPUP_TYPES.reUploadFile ? (
            <ReUploadFilePopupContent
              setPopup={setPopup}
              loading={uploading}
              onApprove={uploadFile}
            />
          ) : popup === POPUP_TYPES.calculate ? (
            <CalculatePopupContent setPopup={setPopup} rowsCount={rows.length} />
          ) : null}
        </Box>
      </Dialog>
    </Box>
  );
};
